import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, Check, ArrowRight, Key, Shield, Zap,
  Code2, Server, CreditCard, AlertTriangle, Globe, Lock
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsAPIIntegration() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          API Integration
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          API Integration Guide
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Connect any e-commerce platform to Commerce Index using the REST API. This guide covers
          authentication, rate limits, credit costs, and code examples for common operations.
        </p>
      </div>

      {/* Getting API Keys */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Getting API Keys
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Key className="w-6 h-6 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
              <p className="text-[#94A3B8]">
                Navigate to <strong className="text-white">Developer Settings &gt; API Keys</strong> in
                your CI dashboard. Click <strong className="text-white">Generate New Key</strong> to create
                an API key. You will also need your Merchant ID, visible at the top of the Developer Settings page.
              </p>
            </div>
            <div className="bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#64748B]">
                  <strong className="text-white">Security:</strong> API keys grant full access to your merchant data.
                  Store them securely in environment variables. Never commit keys to version control or expose
                  them in client-side code.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Authentication */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Authentication
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Every API request requires two headers for authentication:
        </p>
        <div className="space-y-3 mb-4">
          <div className="p-4 rounded-lg bg-[#0B1120] border border-[#22304A] flex items-center gap-4">
            <code className="text-[#3EF1C7] font-mono text-sm font-bold whitespace-nowrap">X-CI-Api-Key</code>
            <span className="text-[#94A3B8] text-sm">Your API key from Developer Settings</span>
          </div>
          <div className="p-4 rounded-lg bg-[#0B1120] border border-[#22304A] flex items-center gap-4">
            <code className="text-[#3EF1C7] font-mono text-sm font-bold whitespace-nowrap">X-CI-Merchant-Id</code>
            <span className="text-[#94A3B8] text-sm">Your merchant identifier</span>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-[#0D1628] border border-[#22304A]">
          <p className="text-sm text-[#94A3B8]">
            <strong className="text-white">Base URL:</strong>{' '}
            <code className="text-[#3EF1C7]">https://api.commerceindex.com/v1</code>
          </p>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Rate Limits by Tier
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Tier</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Rate Limit</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Monthly Credits</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4 text-white font-medium">Free</td>
                  <td className="p-4 text-[#94A3B8]">2 requests/second</td>
                  <td className="p-4 text-[#94A3B8]">1,000 credits</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4 text-white font-medium">Pro</td>
                  <td className="p-4 text-[#94A3B8]">10 requests/second</td>
                  <td className="p-4 text-[#94A3B8]">10,000 credits</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4 text-white font-medium">Scale</td>
                  <td className="p-4 text-[#94A3B8]">50 requests/second</td>
                  <td className="p-4 text-[#94A3B8]">100,000 credits</td>
                </tr>
                <tr>
                  <td className="p-4 text-white font-medium">Enterprise</td>
                  <td className="p-4 text-[#94A3B8]">200 requests/second</td>
                  <td className="p-4 text-[#94A3B8]">1,000,000+ credits</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
        <p className="text-[#94A3B8] text-sm mt-3">
          Rate limit headers are included in every response: <code className="text-[#3EF1C7]">X-RateLimit-Limit</code>,
          <code className="text-[#3EF1C7]"> X-RateLimit-Remaining</code>, and <code className="text-[#3EF1C7]">X-RateLimit-Reset</code>.
        </p>
      </section>

      {/* Credit Costs */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Credit Costs per Endpoint
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Category</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Credits</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4"><span className="text-[#3EF1C7] font-medium">Instant</span></td>
                  <td className="p-4 text-white font-mono">1 credit</td>
                  <td className="p-4 text-[#94A3B8]">Health check, product lookup, inventory level</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4"><span className="text-sky-400 font-medium">Standard</span></td>
                  <td className="p-4 text-white font-mono">5 credits</td>
                  <td className="p-4 text-[#94A3B8]">Create product, update deal, list orders</td>
                </tr>
                <tr className="border-b border-[#22304A]/50">
                  <td className="p-4"><span className="text-amber-400 font-medium">Deep</span></td>
                  <td className="p-4 text-white font-mono">25 credits</td>
                  <td className="p-4 text-[#94A3B8]">CI Score calculation, margin analysis, agent digest</td>
                </tr>
                <tr>
                  <td className="p-4"><span className="text-purple-400 font-medium">Ultra</span></td>
                  <td className="p-4 text-white font-mono">100 credits</td>
                  <td className="p-4 text-[#94A3B8]">Full report generation, competitor analysis, AI recommendations</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Response Headers */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Response Headers
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Every API response includes credit and tier information in the headers:
        </p>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-[#0B1120] border border-[#22304A] flex items-center gap-4">
            <code className="text-[#3EF1C7] font-mono text-xs font-bold whitespace-nowrap">X-CI-Credits-Cost</code>
            <span className="text-[#94A3B8] text-sm">Number of credits consumed by this request</span>
          </div>
          <div className="p-3 rounded-lg bg-[#0B1120] border border-[#22304A] flex items-center gap-4">
            <code className="text-[#3EF1C7] font-mono text-xs font-bold whitespace-nowrap">X-CI-Credits-Remaining</code>
            <span className="text-[#94A3B8] text-sm">Credits remaining in your current billing period</span>
          </div>
          <div className="p-3 rounded-lg bg-[#0B1120] border border-[#22304A] flex items-center gap-4">
            <code className="text-[#3EF1C7] font-mono text-xs font-bold whitespace-nowrap">X-CI-Tier</code>
            <span className="text-[#94A3B8] text-sm">Your current plan tier (free, pro, scale, enterprise)</span>
          </div>
        </div>
      </section>

      {/* JavaScript Example */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          JavaScript Example
        </h2>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(`// Create a product
const createProduct = await fetch('https://api.commerceindex.com/v1/products', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  },
  body: JSON.stringify({
    title: 'Premium Wireless Headphones',
    sku: 'WH-PRO-100',
    price: 79.99,
    compare_at_price: 99.99,
    currency: 'USD',
    category: 'Electronics',
    inventory: { available: 250, location: 'warehouse-east' }
  })
});
const product = await createProduct.json();
console.log('Product created:', product.id);
console.log('Credits remaining:', createProduct.headers.get('X-CI-Credits-Remaining'));

// Get CI Score for a product
const scoreRes = await fetch(\`https://api.commerceindex.com/v1/products/\${product.id}/score\`, {
  headers: {
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  }
});
const score = await scoreRes.json();
console.log(\`CI Score: \${score.score}/1000 (Grade: \${score.grade})\`);

// Create a deal
const dealRes = await fetch('https://api.commerceindex.com/v1/deals', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  },
  body: JSON.stringify({
    title: 'Summer Sale - 20% Off',
    discount_type: 'percentage',
    discount_value: 20,
    product_ids: [product.id],
    starts_at: '2026-03-20T00:00:00Z',
    ends_at: '2026-03-27T00:00:00Z'
  })
});
const deal = await dealRes.json();
console.log('Deal created:', deal.id);`, 'js-example')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'js-example' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`// Create a product
const createProduct = await fetch('https://api.commerceindex.com/v1/products', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  },
  body: JSON.stringify({
    title: 'Premium Wireless Headphones',
    sku: 'WH-PRO-100',
    price: 79.99,
    compare_at_price: 99.99,
    currency: 'USD',
    category: 'Electronics',
    inventory: { available: 250, location: 'warehouse-east' }
  })
});
const product = await createProduct.json();
console.log('Product created:', product.id);
console.log('Credits remaining:', createProduct.headers.get('X-CI-Credits-Remaining'));

// Get CI Score for a product
const scoreRes = await fetch(\`https://api.commerceindex.com/v1/products/\${product.id}/score\`, {
  headers: {
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  }
});
const score = await scoreRes.json();
console.log(\`CI Score: \${score.score}/1000 (Grade: \${score.grade})\`);

// Create a deal
const dealRes = await fetch('https://api.commerceindex.com/v1/deals', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': process.env.CI_API_KEY,
    'X-CI-Merchant-Id': process.env.CI_MERCHANT_ID
  },
  body: JSON.stringify({
    title: 'Summer Sale - 20% Off',
    discount_type: 'percentage',
    discount_value: 20,
    product_ids: [product.id],
    starts_at: '2026-03-20T00:00:00Z',
    ends_at: '2026-03-27T00:00:00Z'
  })
});
const deal = await dealRes.json();
console.log('Deal created:', deal.id);`}</code></pre>
        </div>
      </section>

      {/* Python Example */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Python Example
        </h2>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(`import requests
import os

BASE_URL = "https://api.commerceindex.com/v1"
HEADERS = {
    "X-CI-Api-Key": os.environ["CI_API_KEY"],
    "X-CI-Merchant-Id": os.environ["CI_MERCHANT_ID"],
    "Content-Type": "application/json"
}

# Create a product
product_res = requests.post(f"{BASE_URL}/products", headers=HEADERS, json={
    "title": "Premium Wireless Headphones",
    "sku": "WH-PRO-100",
    "price": 79.99,
    "compare_at_price": 99.99,
    "currency": "USD",
    "category": "Electronics",
    "inventory": {"available": 250, "location": "warehouse-east"}
})
product = product_res.json()
print(f"Product created: {product['id']}")
print(f"Credits remaining: {product_res.headers['X-CI-Credits-Remaining']}")

# Get CI Score
score_res = requests.get(
    f"{BASE_URL}/products/{product['id']}/score",
    headers=HEADERS
)
score = score_res.json()
print(f"CI Score: {score['score']}/1000 (Grade: {score['grade']})")

# Create a deal
deal_res = requests.post(f"{BASE_URL}/deals", headers=HEADERS, json={
    "title": "Summer Sale - 20% Off",
    "discount_type": "percentage",
    "discount_value": 20,
    "product_ids": [product["id"]],
    "starts_at": "2026-03-20T00:00:00Z",
    "ends_at": "2026-03-27T00:00:00Z"
})
deal = deal_res.json()
print(f"Deal created: {deal['id']}")`, 'python-example')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'python-example' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`import requests
import os

BASE_URL = "https://api.commerceindex.com/v1"
HEADERS = {
    "X-CI-Api-Key": os.environ["CI_API_KEY"],
    "X-CI-Merchant-Id": os.environ["CI_MERCHANT_ID"],
    "Content-Type": "application/json"
}

# Create a product
product_res = requests.post(f"{BASE_URL}/products", headers=HEADERS, json={
    "title": "Premium Wireless Headphones",
    "sku": "WH-PRO-100",
    "price": 79.99,
    "compare_at_price": 99.99,
    "currency": "USD",
    "category": "Electronics",
    "inventory": {"available": 250, "location": "warehouse-east"}
})
product = product_res.json()
print(f"Product created: {product['id']}")
print(f"Credits remaining: {product_res.headers['X-CI-Credits-Remaining']}")

# Get CI Score
score_res = requests.get(
    f"{BASE_URL}/products/{product['id']}/score",
    headers=HEADERS
)
score = score_res.json()
print(f"CI Score: {score['score']}/1000 (Grade: {score['grade']})")

# Create a deal
deal_res = requests.post(f"{BASE_URL}/deals", headers=HEADERS, json={
    "title": "Summer Sale - 20% Off",
    "discount_type": "percentage",
    "discount_value": 20,
    "product_ids": [product["id"]],
    "starts_at": "2026-03-20T00:00:00Z",
    "ends_at": "2026-03-27T00:00:00Z"
})
deal = deal_res.json()
print(f"Deal created: {deal['id']}")`}</code></pre>
        </div>
      </section>

      {/* cURL Example */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          cURL Example
        </h2>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(`# Create a product
curl -X POST https://api.commerceindex.com/v1/products \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID" \\
  -d '{
    "title": "Premium Wireless Headphones",
    "sku": "WH-PRO-100",
    "price": 79.99,
    "compare_at_price": 99.99,
    "currency": "USD",
    "category": "Electronics"
  }'

# Get CI Score for a product
curl https://api.commerceindex.com/v1/products/prod_abc123/score \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID"

# Response:
# {
#   "product_id": "prod_abc123",
#   "score": 847,
#   "grade": "A",
#   "components": {
#     "fulfillment": 91,
#     "communication": 88,
#     "consistency": 85,
#     "policy_adherence": 82
#   },
#   "trend": "improving",
#   "computed_at": "2026-03-17T10:30:00Z"
# }

# Create a deal
curl -X POST https://api.commerceindex.com/v1/deals \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID" \\
  -d '{
    "title": "Summer Sale - 20% Off",
    "discount_type": "percentage",
    "discount_value": 20,
    "product_ids": ["prod_abc123"],
    "starts_at": "2026-03-20T00:00:00Z",
    "ends_at": "2026-03-27T00:00:00Z"
  }'`, 'curl-example')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'curl-example' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Create a product
curl -X POST https://api.commerceindex.com/v1/products \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID" \\
  -d '{
    "title": "Premium Wireless Headphones",
    "sku": "WH-PRO-100",
    "price": 79.99,
    "compare_at_price": 99.99,
    "currency": "USD",
    "category": "Electronics"
  }'

# Get CI Score for a product
curl https://api.commerceindex.com/v1/products/prod_abc123/score \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID"

# Response:
# {
#   "product_id": "prod_abc123",
#   "score": 847,
#   "grade": "A",
#   "components": {
#     "fulfillment": 91,
#     "communication": 88,
#     "consistency": 85,
#     "policy_adherence": 82
#   },
#   "trend": "improving",
#   "computed_at": "2026-03-17T10:30:00Z"
# }

# Create a deal
curl -X POST https://api.commerceindex.com/v1/deals \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Api-Key: $CI_API_KEY" \\
  -H "X-CI-Merchant-Id: $CI_MERCHANT_ID" \\
  -d '{
    "title": "Summer Sale - 20% Off",
    "discount_type": "percentage",
    "discount_value": 20,
    "product_ids": ["prod_abc123"],
    "starts_at": "2026-03-20T00:00:00Z",
    "ends_at": "2026-03-27T00:00:00Z"
  }'`}</code></pre>
        </div>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/channel-setup">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Channel Setup <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Connect messaging platforms for command-based management</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/ci-score">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">CI Score <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Learn how the Commerce Index score is calculated</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
